import { inject, Injectable } from '@angular/core';
import { Contract } from '@models/contract';
import { Insurant, isArrayOfInsurants } from '@models/insurant';
import { PoolObject, CustomerService, InsurenetInterfaceService } from '@core';
import { BehaviorSubject, map, Observable, switchMap, take, tap } from 'rxjs';
import { ShareableFn, toShareable } from '@co-3/ctrl-ui/common';

type ContractQueryFn = (contract: Contract) => boolean;

@Injectable({
    providedIn: 'root',
})
export class ContractsService {
    private _contracts$: BehaviorSubject<Contract[]> = new BehaviorSubject<Contract[]>([]);

    public contracts$: Observable<Contract[]> = this._contracts$.asObservable();

    private customerService = inject(CustomerService);
    private insurenetInterfaceService = inject(InsurenetInterfaceService);

    private shareableContractRequest: ShareableFn<Contract[]> = this.getShareableContractRequest();

    public reset(): void {
        this.shareableContractRequest = null;
        this._contracts$.next([]);
    }

    public fetch(): Observable<Contract[]> {
        const userId = this.customerService.customer?.id;
        if (userId == null) {
            throw new Error('ContractsService.fetchAll: failed to fetch contracts. no customer selected.');
        }
        if (this.shareableContractRequest === null) {
            this.shareableContractRequest = this.getShareableContractRequest();
        }
        return this.shareableContractRequest();
    }

    public get(): Observable<Contract[]>;
    public get(contractId: number): Observable<Contract>;
    public get(queryFn: ContractQueryFn): Observable<Contract[]>;
    public get(query?: number | ContractQueryFn): Observable<Contract> | Observable<Contract[]> {
        let contractsPipe$: Observable<Contract[]> = this.contracts$;
        if (!this._contracts$.getValue().length) {
            contractsPipe$ = this.fetch().pipe(switchMap(() => this.contracts$));
        }
        if (typeof query === 'number') {
            return contractsPipe$.pipe(
                map((contracts: Contract[]) => contracts.find((contract: Contract) => contract.id === query)),
            );
        } else if (typeof query === 'function') {
            return contractsPipe$.pipe(
                map((contracts: Contract[]) => contracts.filter((contract: Contract) => query(contract))),
            );
        }
        return contractsPipe$;
    }

    public updateInsurants(contractId: number, insurants: Insurant[]): Observable<Contract> {
        return this.insurenetInterfaceService.updateInsurants(contractId, insurants).pipe(
            tap((updatedInsurants: Insurant[]) => this.updateContract(contractId, updatedInsurants)),
            switchMap(() => this.get(contractId)),
            take(1),
        );
    }

    public updateInsuredObjects(contractId: number, insuredObjects: PoolObject[]): Observable<Contract> {
        return this.insurenetInterfaceService.updateObjectPool(contractId, insuredObjects).pipe(
            tap((updatedInsuredObjects: PoolObject[]) => this.updateContract(contractId, updatedInsuredObjects)),
            switchMap(() => this.get(contractId)),
            take(1),
        );
    }

    private updateContract(contractId: number, insurants: Insurant[]): void;
    private updateContract(contractId: number, insuredObjects: PoolObject[]): void;
    private updateContract(contractId: number, entities: Insurant[] | PoolObject[]): void {
        const index = this._contracts$.getValue().findIndex((contract: Contract) => contract.id === contractId);
        if (index !== -1) {
            const next = [...this._contracts$.getValue()];
            if (isArrayOfInsurants(entities)) {
                next[index] = { ...next[index], insurants: entities };
            } else {
                next[index] = { ...next[index], objectPool: entities };
            }
            this._contracts$.next(next);
        }
    }

    private getShareableContractRequest(): ShareableFn<Contract[]> {
        return toShareable(
            () =>
                this.insurenetInterfaceService
                    .getContracts()
                    .pipe(tap((contracts: Contract[]) => this._contracts$.next(contracts))),
            { keepFor: '5 min' },
        );
    }
}
